import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { client } from './api/client';
import { useAuthStore } from './store/authStore';
import { MainLayout } from './layouts/MainLayout';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { SubjectsPage } from './pages/SubjectsPage';
import { TopicsPage } from './pages/TopicsPage';
import { PracticePage } from './pages/PracticePage';
import { MistakesPage } from './pages/MistakesPage';
import { BadgesPage } from './pages/BadgesPage';
import { ParentPage } from './pages/ParentPage';
import { WeeklyReportPage } from './pages/WeeklyReportPage';
import { DailyChallengePage } from './pages/DailyChallengePage';
import { ExportPage } from './pages/ExportPage';
import { PhoneLoginPage } from './pages/PhoneLoginPage';
import QuestionGeneratorPage from './pages/QuestionGeneratorPage';
import { MaterialsPage } from './pages/MaterialsPage';
import { LearningDashboard } from './pages/LearningDashboard';
import { VCEMathematicsPage } from './pages/VCEMathematicsPage';
import { KnowledgeGraphPage } from './pages/KnowledgeGraphPage';
import { AISolverPage } from './pages/AISolverPage';
import { MaterialImportPage } from './pages/MaterialImportPage';
import StudyProgramPage from './pages/StudyProgramPage';
import StudyProgramsListPage from './pages/StudyProgramsListPage';
import { ProgressPage } from './pages/ProgressPage';
import { AIRecommendPage } from './pages/AIRecommendPage';
import { LeaderboardPage } from './pages/LeaderboardPage';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isChecking } = useAuthStore();

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { user, setUser, setChecking } = useAuthStore();

  useEffect(() => {
    restoreSession();
  }, []);

  async function restoreSession() {
    // Skip network request if user exists locally
    if (useAuthStore.getState().user) {
      setChecking(false);
      return;
    }
    try {
      const session = await client.auth.getSession();
      if (session.data?.user) {
        setUser({
          id: session.data.user.id,
          email: session.data.user.email,
          name: session.data.user.name || '',
        });
      }
    } finally {
      setChecking(false);
    }
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/phone" element={<PhoneLoginPage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
        </Route>

        {/* Placeholder routes - will be implemented later */}
        <Route path="/subjects" element={<MainLayout />}>
          <Route index element={<SubjectsPage />} />
        </Route>
        <Route path="/subjects/:subject" element={<MainLayout />}>
          <Route index element={<TopicsPage />} />
        </Route>
        <Route path="/practice/:topicId" element={<MainLayout />}>
          <Route index element={<PracticePage />} />
        </Route>
        <Route path="/exams/*" element={<MainLayout />}>
          <Route path="*" element={<ComingSoon title="模拟考试" />} />
        </Route>
        <Route path="/mistakes" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<MistakesPage />} />
        </Route>
        <Route path="/flashcards" element={<MainLayout />}>
          <Route index element={<ComingSoon title="记忆卡片" />} />
        </Route>
        <Route path="/leaderboard" element={<MainLayout />}>
          <Route index element={<LeaderboardPage />} />
        </Route>
        <Route path="/community" element={<MainLayout />}>
          <Route index element={<ComingSoon title="学习社区" />} />
        </Route>
        <Route path="/challenges" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<DailyChallengePage />} />
        </Route>
        <Route path="/export" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<ExportPage />} />
        </Route>
        <Route path="/badges" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<BadgesPage />} />
        </Route>
        <Route path="/parent" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<ParentPage />} />
        </Route>
        <Route path="/report" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<WeeklyReportPage />} />
        </Route>
        <Route path="/pomodoro" element={<MainLayout />}>
          <Route index element={<ComingSoon title="番茄钟" />} />
        </Route>
        <Route path="/generator" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
        }>
            <Route index element={<QuestionGeneratorPage />} />
        </Route>
        <Route path="/materials" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
        }>
            <Route index element={<MaterialsPage />} />
        </Route>
        <Route path="/learning" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
        }>
            <Route index element={<LearningDashboard />} />
        </Route>
        <Route path="/vce/mathematics" element={<MainLayout />}>
            <Route index element={<VCEMathematicsPage />} />
        </Route>
        <Route path="/knowledge-graph" element={<MainLayout />}>
            <Route index element={<KnowledgeGraphPage />} />
        </Route>
        <Route path="/solver" element={<MainLayout />}>
            <Route index element={<AISolverPage />} />
        </Route>
        <Route path="/import" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
        }>
            <Route index element={<MaterialImportPage />} />
        </Route>
        <Route path="/study-program/:id" element={<MainLayout />}>
            <Route index element={<StudyProgramPage />} />
        </Route>
        <Route path="/progress" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<ProgressPage />} />
          </Route>
          <Route path="/ai-recommend" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<AIRecommendPage />} />
          </Route>
        <Route path="/settings" element={<MainLayout />}>
          <Route index element={<ComingSoon title="设置" />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Placeholder component for upcoming features
function ComingSoon({ title }: { title: string }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4">🚧</div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">{title}</h2>
        <p className="text-slate-600">功能开发中，敬请期待...</p>
      </div>
    </div>
  );
}

export default App;
